import { inject, Injectable } from '@angular/core';
import { toObservable } from '@angular/core/rxjs-interop';
import { environment } from '@env/environment';
import { WalkMeData, userRoleToWalkMeUserRole } from '@core/walk-me/models/walk-me-data.interface';
import { BehaviorSubject, combineLatest, filter } from 'rxjs';
import { WalkMeWindow } from '@core/walk-me/models/custom-window.type';
import { Permission } from '@kaseya-shared';
import { AuthorizationService } from '@core/authorization/services/authorization.service';
import { PermissionsFacade } from '@core/permissions/facades/permissions.facade';
import { ApplicationVersionFacade } from '@core/application-version/facades/application-version.facade';
import { TranslateService } from '@ngx-translate/core';
import { UserInfoFacade } from '@core/user-info/facades/user-info.facade';
import { PaymentManagementActiveFacade } from '@layout/payment-management-active/facades/payment-management-active.facade';
import { WalkMeFacade } from '@core/walk-me/facades/walk-me.facade';
import { getExternalDomains } from '@core/walk-me/utils/get-external-domains';
import { asNonUndefined, filterIsNotUndefined, isNotUndefined } from '@kaseya-utils';

@Injectable({
  providedIn: 'root',
})
export class WalkMeInitFacade {
  private readonly authorizationService = inject(AuthorizationService);
  private readonly applicationVersionFacade = inject(ApplicationVersionFacade);
  private readonly translateService = inject(TranslateService);
  private readonly walkMeFacade = inject(WalkMeFacade);
  private readonly userInfoFacade = inject(UserInfoFacade);
  private readonly paymentManagementActiveFacade = inject(PaymentManagementActiveFacade);
  private readonly permissionsFacade = inject(PermissionsFacade);
  private readonly permissions$ = toObservable(this.permissionsFacade.permissions).pipe(filterIsNotUndefined());
  private readonly isPaymentManagementActive$ = this.paymentManagementActiveFacade.isPaymentManagementActive$.pipe(filterIsNotUndefined());
  private readonly paymentManagementPermissions = [Permission.PaymentManagementView, Permission.PaymentManagementModify];
  private readonly isInternalUser$ = new BehaviorSubject<boolean | undefined>(undefined);
  private window = window as unknown as WalkMeWindow;
  private walkMeSnippetCreated = false;

  constructor() {
    this.authorizationService.onLogin.subscribe(() => this.initWalkMe());
    this.authorizationService.onLogout.subscribe(() => this.removeWalkMe());
    this.registerWalkMeReadyListener();
  }

  private initWalkMe(): void {
    this.walkMeFacade.loadWalkMeDataIfNotLoaded();
    this.setWalkMeVariables();
    this.setWalkMeConfig();
  }

  private registerWalkMeReadyListener(): void {
    this.window.walkme_ready = (): void => {
      const userEmailDomain = this.userInfoFacade.userEmailDomain();
      if (userEmailDomain) {
        this.isInternalUser$.next(getExternalDomains(this.window.WalkMeAPI).includes(userEmailDomain));
        return;
      }
      this.isInternalUser$.next(false);
    };
  }

  private setWalkMeVariables(): void {
    const nowDate = new Date();
    const [utcDate] = nowDate.toISOString().split('T');
    const utcTime = this.getUTCTime(nowDate);

    combineLatest([
      this.applicationVersionFacade.applicationVersion$,
      this.walkMeFacade.walkMeData$,
      this.isPaymentManagementActive$,
      this.isInternalUser$,
      this.permissions$,
    ])
      .pipe(filter(([applicationVersion, walkMeDataResponse]) => isNotUndefined(applicationVersion) && isNotUndefined(walkMeDataResponse)))
      .subscribe(([applicationVersion, walkMeDataResponse, isPaymentManagementActive, isInternalUser, permissions]) => {
        this.window.walkMeData = {
          userId: this.userInfoFacade.userTrackingId(),
          tenantId: this.userInfoFacade.organizationTrackingId(),
          utcDate,
          utcTime,
          applicationVersionNumber: applicationVersion,
          firstName: this.userInfoFacade.userFirstName(),
          lastName: this.userInfoFacade.userLastName(),
          emailAddress: walkMeDataResponse?.encryptedEmailAddress,
          companyName: this.userInfoFacade.organizationName(),
          role: walkMeDataResponse ? userRoleToWalkMeUserRole[asNonUndefined(walkMeDataResponse.roleId)] : '',
          featureAccess: permissions
            .filter(permission => (this.paymentManagementPermissions.includes(permission) ? isPaymentManagementActive : true))
            .join('|'),
          locale: this.translateService.getCurrentLang(),
          userCreationDaysElapsed: walkMeDataResponse?.userCreationDaysElapsed,
          databaseCreationDaysElapsed: walkMeDataResponse?.organizationCreationDaysElapsed,
          usersCount: walkMeDataResponse?.usersCount,
          isRootUser: walkMeDataResponse?.isRootUser,
          salesforceId: walkMeDataResponse?.salesforceId,
          isKaseya365: walkMeDataResponse?.isKaseya365,
          isAccountTrial: walkMeDataResponse?.isAccountTrial,
          accountEnabledAutopay: walkMeDataResponse?.isAccountAutopayEnabled,
          isInternalUser,
        } as WalkMeData;

        if (!this.walkMeSnippetCreated) {
          this.walkMeSnippetCreated = true;
          this.createWalkMeSnippet();
        }
      });
  }

  private createWalkMeSnippet(): void {
    const walkMeScript = document.createElement('script');
    walkMeScript.async = true;
    walkMeScript.src = environment.walkMeSnippetUrl;
    document.head.prepend(walkMeScript);
  }

  private setWalkMeConfig(): void {
    this.window._walkmeConfig = { smartLoad: true };
  }

  private removeWalkMe(): void {
    this.window._walkMe?.removeWalkMe?.();
    this.window.walkMeData = undefined;
    this.walkMeSnippetCreated = false;
  }

  private getUTCTime(date: Date): string {
    return date
      .toLocaleTimeString(undefined, {
        timeStyle: 'short',
        hour12: false,
        timeZone: 'UTC',
      })
      .replace(':', '');
  }
}
